import React from 'react';
import { motion } from 'framer-motion';
import ImpactTracker from '../components/ImpactTracker';
import ProblemPotential from '../components/ProblemPotential';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7 }
};

const steps = [
  {
    url: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/3imoedx8_2.jpg.jpeg',
    label: 'Skill Development'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/8w9zyj1w_4.jpg.jpeg',
    label: 'Material Processing'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/0tm8s85r_1.png',
    label: 'Quality Assurance'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/lu82awcv_IMG_7447.jpeg',
    label: 'Inventory Management'
  }
];

// The woman in the provided portrait — shown in the Yuwa-style zoom section
const PORTRAIT_IMG = 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/9gze9wqf_IMG_7402.jpeg';

const Impact = () => {
  return (
    <div data-testid="impact-page" className="min-h-screen bg-[#F4F4F4]">
      {/* 1. Hero - Dark full screen */}
      <section className="bg-[#641b4d] min-h-screen flex items-center px-6">
        <div className="max-w-5xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.3em] text-[#bc64aa] mb-8"
          >
            Our Impact
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="impact-heading"
            className="text-4xl md:text-5xl lg:text-7xl font-light tracking-tighter text-white leading-[1.05]"
          >
            Every planter is built by a woman earning her own livelihood.
          </motion.h1>
        </div>
      </section>

      {/* 2. Problem & Potential — Yuwa-style zoom-in portrait with staggered reveal */}
      <ProblemPotential image={PORTRAIT_IMG} />

      {/* 3. Training philosophy — updated copy */}
      <section className="py-24 lg:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#bc64aa] mb-10">
            What We Do
          </motion.p>
          <motion.p {...fadeUp} className="text-2xl md:text-3xl lg:text-4xl font-light text-[#641b4d] leading-snug tracking-tight">
            We don't just train — we build independent production capability.
          </motion.p>
          <motion.p {...fadeUp} className="text-base md:text-lg text-[#7a5e75] leading-relaxed mt-6 max-w-2xl">
            Women learn to manufacture, price, and sell — not just assist.
          </motion.p>
        </div>
      </section>

      {/* 6. Four pillars - Large visual grid */}
      <section className="px-6 pb-24 lg:pb-32">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                data-testid={`impact-step-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group relative overflow-hidden"
              >
                <img src={step.url} alt={step.label} className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="text-white text-xs uppercase tracking-widest">{step.label}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. LIVE IMPACT TRACKER */}
      <ImpactTracker variant="dark" title="Impact, in real time" />

      {/* 8. Closing vision */}
      <section className="py-24 lg:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#bc64aa] mb-10">
            Our Vision
          </motion.p>
          <motion.p {...fadeUp} className="text-2xl md:text-3xl font-light text-[#641b4d] leading-snug tracking-tight">
            Behind every planter is a woman trained, equipped, and empowered to build her own livelihood — and yours.
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default Impact;
